import React from 'react';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faRefresh } from '@fortawesome/pro-thin-svg-icons';
import { Flex } from 'antd';
import {
	CargoContractStatus,
	CargoContractStatusTypes,
	CargoContractSides,
	CargoTermTypes,
	CargoUnitLabels,
	CargoUnitSingularLabels,
	CargoUnitTypes,
	Currencies,
	EstimateStatus,
	FreightRateType,
} from '@shared/utils/constants';
import { formatNumber } from '@shared/utils/formatNumber';
import { formatCurrency } from '@shared/utils/currency';
import { Values } from '@shared/utils/objectEnums';
import type { GetCounterpartiesResponse } from '@api/features/counterparties/getCounterparties';
import type { Port } from '@api/utils/ports';
import type { EditableCargo } from '@api/features/cargos/updateCargo';
import { UserData } from '@api/utils/sequelize/calculateUserData';
import { GetEstimateDetailsResponse } from '@api/features/estimates/getEstimateDetails';
import EmptyText from '@client/components/EmptyText';
import { getPortOptions } from '@client/utils/getPortAndRangeOptions';
import getFixingCompanyOptions from '@client/utils/getFixingCompanyOptions';
import Button from '@client/components/Button';
import TooltipIcon from '@client/components/TooltipIcon';
import styles from '../CargoDetailsScreen.module.css';
import { ModalModes } from '../CargoDetailsScreen';
import getRelevantCargoTypes from './getRelevantCargoTypes';

const getKeyCargoDetailsFields = ({
	mode,
	cargoDetails,
	charterers,
	isTceLocked,
	ports,
	handleRateChange,
	baseCurrency,
	inEstimate,
	userData,
	estimate,
	handleCargoTypeSelect,
}: {
	mode: ModalModes;
	cargoDetails: EditableCargo;
	charterers: GetCounterpartiesResponse | undefined;
	isTceLocked?: boolean;
	ports: Port[] | undefined;
	handleRateChange: () => void;
	baseCurrency: Values<typeof Currencies>;
	inEstimate?: boolean;
	userData: UserData | undefined;
	estimate: GetEstimateDetailsResponse | null;
	handleCargoTypeSelect: (v: number) => void;
}) => {
	if (cargoDetails == null) {
		return [];
	}

	const creatingCargo = mode === 'creatingOnContract';
	const addonAfterFreightRate =
		cargoDetails.freightType === FreightRateType.PER_UNIT ?
			`/ ${CargoUnitSingularLabels[cargoDetails.unit]}` :
			'';
	const portOptions = getPortOptions(ports ?? []);
	const disabled = cargoDetails?.status === EstimateStatus.Fixed && cargoDetails.voyageId == null;
	const fixingCompanies = userData?.fixingCompanies ?? [];
	const fixingCompanyEditable = !disabled &&
		!creatingCargo &&
		cargoDetails.voyageId == null &&
		cargoDetails.contractStatus !== CargoContractStatusTypes.OnVoyage &&
		cargoDetails.contractSide !== CargoContractSides.VC_IN;

	const getCargoContractStatusOptions = () =>
		Object.values(CargoContractStatusTypes)
			.filter((status) => status !== CargoContractStatusTypes.OnVoyage)
			.map((status) => ({
				label: status,
				value: status,
			}));

	const options: {
		label: CargoContractStatus;
		value: CargoContractStatus;
	}[] = getCargoContractStatusOptions();

	if (cargoDetails.contractStatus === CargoContractStatusTypes.OnVoyage) {
		options.push({
			label: CargoContractStatusTypes.OnVoyage,
			value: CargoContractStatusTypes.OnVoyage,
		});
	}

	const hasCargoRelet = cargoDetails.contractSide === CargoContractSides.VC_IN;
	const relevantCargoTypes = getRelevantCargoTypes(userData, estimate);

	return [
		{
			required: creatingCargo,
			key: 'contractStatus',
			label: 'Contract Status',
			type: 'select',
			value: cargoDetails.contractStatus,
			options,
			inputProps: {
				showSearch: true,
				tabIndex: 1,
				disabled: cargoDetails.contractStatus === CargoContractStatusTypes.OnVoyage,
			},
			editable: !disabled,
		},
		{
			key: 'type',
			label: 'Commodity',
			type: 'autocomplete',
			defaultValue: cargoDetails.type,
			editable: !disabled,
			inputProps: {
				onSelect: (v: string) => {
					const cargoType = relevantCargoTypes.find((ct) => ct.name === v);

					if (cargoType?.stowageFactorCBFT == null) {
						return;
					}

					handleCargoTypeSelect(cargoType.stowageFactorCBFT);
				},
				defaultValue: cargoDetails.type,
				filterOption: (inputValue: string, option: { label: string }) => (
					option?.label?.toUpperCase().includes(inputValue.toUpperCase()) ?? false
				),
				tabIndex: 2,
				required: creatingCargo,
				options: relevantCargoTypes.map((ct) => ({
					label: ct.name,
					value: ct.name,
				})),
			},
		},
		{
			required: creatingCargo,
			key: 'quantity',
			label: 'CP Quantity',
			type: 'number',
			value: cargoDetails.quantity,
			render: () => (cargoDetails.quantity != null ?
				`${formatNumber(cargoDetails.quantity, { separateThousands: true })} ${CargoUnitLabels[cargoDetails.unit]}` : ''),
			editable: !disabled,
			inputProps: {
				addonAfter: CargoUnitLabels[cargoDetails.unit],
				separateThousands: true,
				tabIndex: 3,
				disabled: cargoDetails.contractSide === CargoContractSides.VC_IN,
			},
		},
		{
			key: 'totalExpectedCargo',
			label: (
				<Flex>
					<p style={{ margin: 0, padding: 0 }}>Total expected cargo</p>
					<TooltipIcon>
						This value is derived from the intake calculator
					</TooltipIcon>
				</Flex>
			),
			value: cargoDetails.totalExpectedCargo,
			render: () => (cargoDetails.totalExpectedCargo != null ?
				`${formatNumber(cargoDetails.totalExpectedCargo, { separateThousands: true })} ${CargoUnitLabels[cargoDetails.unit]}` : ''),
		},
		{
			required: creatingCargo,
			key: 'unit',
			label: 'Unit',
			type: 'select',
			value: cargoDetails.unit,
			options: Object.values(CargoUnitTypes).map((c) => ({
				label: CargoUnitLabels[c],
				value: c,
			})),
			render: () => (CargoUnitLabels[cargoDetails.unit]),
			editable: !disabled,
			inputProps: {
				showSearch: true,
				tabIndex: 4,
				disabled: cargoDetails.contractSide === CargoContractSides.VC_IN,
			},
		},
		{
			key: 'secondaryUnit',
			label: 'Secondary Unit',
			type: 'select',
			value: cargoDetails.secondaryUnit,
			options: Object.values(CargoUnitTypes).map((c) => ({
				label: CargoUnitLabels[c],
				value: c,
			})),
			render: () => (CargoUnitLabels[cargoDetails.unit]),
			editable: !disabled,
			inputProps: {
				showSearch: true,
				tabIndex: 5,
			},
		},
		{
			required: creatingCargo,
			key: 'freightRate',
			label: 'Freight Rate',
			type: 'currency',
			value: cargoDetails.freightRate,
			inputProps: {
				currency: cargoDetails.currency,
				disabled: isTceLocked,
				tabIndex: 6,
			},
			editable: !disabled,
			render: () => (
				<>
					{cargoDetails.freightRate != null ?
						`${formatCurrency(cargoDetails.freightRate, cargoDetails.currency)}
                                ${addonAfterFreightRate}
                            ` : (<EmptyText />)}
				</>
			),
		},
		{
			required: creatingCargo,
			key: 'currency',
			label: 'Currency',
			type: 'select',
			value: cargoDetails.currency,
			options: Object.values(Currencies).map((c) => ({
				label: Currencies[c],
				value: c,
			})),
			inputProps: {
				disabled: isTceLocked,
				showSearch: true,
				tabIndex: 7,
			},
			editable: !disabled,
		},
		...(cargoDetails.currency !== baseCurrency ?
			[{
				key: 'exchangeRate',
				label: `Exchange Rate (${cargoDetails.currency} to ${baseCurrency})`,
				type: 'number',
				value: cargoDetails.exchangeRate,
				inputProps: {
					disabled: isTceLocked || disabled,
					tabIndex: 8,
					addonAfter: (
						<Button
							type="link"
							className={styles.refreshButton}
							onClick={handleRateChange}
							icon={(<FontAwesomeIcon color="#1677ff" icon={faRefresh} />)}
						>
							Refresh
						</Button>
					),
				},
				editable: !disabled,
			}] : []
		),
		{
			required: creatingCargo,
			key: 'freightType',
			label: 'Freight Type',
			type: 'select',
			value: cargoDetails.freightType,
			options: [
				{
					label: `Per ${cargoDetails.unit != null ? CargoUnitSingularLabels[cargoDetails.unit] ?? 'Unit' : 'Unit'}`,
					value: FreightRateType.PER_UNIT,
				},
				{
					label: 'Lumpsum',
					value: FreightRateType.LUMPSUM,
				},
			],
			render: () => (
				<>
					{cargoDetails.freightType != null ?
						(
							cargoDetails.freightType === FreightRateType.PER_UNIT ?
								`Per ${cargoDetails.unit != null ? CargoUnitSingularLabels[cargoDetails.unit] ?? 'Unit' : 'Unit'}` :
								FreightRateType.LUMPSUM
						) : (<EmptyText />)}
				</>
			),
			inputProps: {
				showSearch: true,
				tabIndex: 9,
			},
			editable: !disabled,
		},
		{
			key: 'terms',
			label: 'Terms',
			type: 'select',
			value: cargoDetails.terms,
			editable: !disabled,
			render: () => <>{cargoDetails.terms}</>,
			options: Object.keys(CargoTermTypes)
				.map((terms) => ({
					label: terms,
					value: terms,
				})),
			inputProps: {
				placeholder: 'Select Terms',
				showSearch: true,
				tabIndex: 10,
			},
		},
		{
			required: creatingCargo,
			key: 'charterer',
			label: 'Charterer',
			type: 'select',
			value: cargoDetails.charterer,
			editable: !disabled,
			render: () => <>{cargoDetails.chartererName || (<EmptyText />)}</>,
			options: charterers?.map((c) => ({
				label: c.name,
				value: c.id,
			})),
			inputProps: {
				showSearch: true,
				placeholder: 'Select Charterer',
				tabIndex: 11,
			},
		},
		{
			key: 'fixingCompanyId',
			label: 'Fixing Company',
			type: 'select',
			value: cargoDetails.fixingCompanyId,
			editable: fixingCompanyEditable,
			render: () => (
				<>
					{creatingCargo ?
						'Same as contract' :
						fixingCompanies.find((c) => c.id === cargoDetails.fixingCompanyId)?.name ?? (<EmptyText />)}
				</>
			),
			options: getFixingCompanyOptions(fixingCompanies, cargoDetails.fixingCompanyId),
			inputProps: {
				showSearch: true,
				placeholder: 'Select Fixing Company',
			},
		},
		{
			required: creatingCargo,
			label: 'Loading Ports',
			value: (cargoDetails.loadingPorts ?? []).map((lp) => (typeof lp !== 'number' && 'id' in lp ? lp.id : lp)),
			key: 'loadingPorts',
			options: portOptions,
			multiple: true,
			inputProps: {
				showSearch: true,
				allowClear: false,
				tabIndex: 12,
				disabled: cargoDetails.contractSide === CargoContractSides.VC_IN,
			},
			disableOnDeselect: !inEstimate,
			editable: !disabled,
			type: 'select',
		},
		{
			required: creatingCargo,
			label: 'Discharge Ports',
			key: 'dischargePorts',
			multiple: true,
			options: portOptions,
			value: (cargoDetails.dischargePorts ?? []).map((lp) => (typeof lp !== 'number' && 'id' in lp ? lp.id : lp)),
			inputProps: {
				showSearch: true,
				allowClear: false,
				tabIndex: 13,
				disabled: cargoDetails.contractSide === CargoContractSides.VC_IN,
			},
			disableOnDeselect: !inEstimate,
			editable: !disabled,
			type: 'select',
		},
		{
			label: 'CP Date',
			value: cargoDetails.cpDate,
			key: 'cpDate',
			editable: !disabled,
			type: 'date',
		},
		{
			label: 'BL Number',
			value: cargoDetails.blNumber,
			key: 'blNumber',
			type: 'text',
			editable: true,
			inputProps: {
				disabled: hasCargoRelet,
			},
		},
		{
			label: 'Stowage Factor',
			value: cargoDetails.stowageFactor,
			key: 'stowageFactor',
			type: 'number',
			editable: true,
			inputProps: {
				suffix: 'cbft/ton',
			},
		},
		{
			label: 'Description',
			value: cargoDetails.description,
			key: 'description',
			type: 'text',
			editable: true,
		},
	];
};

export default getKeyCargoDetailsFields;
